import React, {useEffect} from 'react';
import {Link} from '@amzn/awsui-components-react';
import ReactTooltip from 'react-tooltip';
import {AvailabilityConsumerDetails} from '../../../backend/model';
import {compareNumericString} from '../../../utils/sorting';
import {AcLink} from '../../TableItemLink';
import {operators, TableDetails, TableInput, useTableCreation} from '../../TableTemplate';
import {getRablUrl} from '../../../backend/internalUrls';

const TABLE_ID = 'iapPage-acsTable';

const COLUMN_DEFINITIONS = (fnsku?: string, programDescriptions?: {[key: string]: string}) => [
    {
        id: 'id',
        header: 'Availability Consumer',
        cell: (ac: AvailabilityConsumerDetails): JSX.Element => <AcLink ac={ac.id} fnsku={fnsku}/>,
        sortingField: 'id',
    },
    {
        id: 'marketplaceId',
        header: 'Marketplace',
        cell: (ac: AvailabilityConsumerDetails) => ac.marketplaceId,
        sortingComparator: (a: AvailabilityConsumerDetails, b: AvailabilityConsumerDetails): number =>
            compareNumericString(a.marketplaceId, b.marketplaceId),
        sortingField: 'marketplaceId'
    },
    {
        id: 'merchantCustomerId',
        header: 'Merchant',
        cell: (ac: AvailabilityConsumerDetails) => ac.merchantCustomerId,
        sortingComparator: (a: AvailabilityConsumerDetails, b: AvailabilityConsumerDetails): number =>
            compareNumericString(a.merchantCustomerId, b.merchantCustomerId),
        sortingField: 'merchantCustomerId'
    },
    {
        id: 'fulfillmentProgramId',
        header: 'Program',
        cell: (ac: AvailabilityConsumerDetails): JSX.Element | string => 
            ac.fulfillmentProgramId ? (
                <span data-tip={programDescriptions?.[ac.fulfillmentProgramId] || 'Program does not exist in PJS.'} data-for="program-tooltip" style={{textDecoration: 'underline', cursor: 'help'}}>
                    {ac.fulfillmentProgramId}
                </span>
            ) : ac.fulfillmentProgramId,
        sortingField: 'fulfillmentProgramId',
    },
    {
        id: 'fulfillmentRegionId',
        header: 'Region',
        cell: (ac: AvailabilityConsumerDetails): JSX.Element | string => 
            ac.fulfillmentRegionId ? 
                <Link href={getRablUrl(ac.fulfillmentRegionId)} external>{ac.fulfillmentRegionId}</Link> : 
                ac.fulfillmentRegionId,
        sortingField: 'fulfillmentRegionId',
    },
];

const FILTERING_PROPERTIES = [
    {
        propertyLabel: 'Availability Consumer',
        key: 'id',
        groupValuesLabel: 'AC ID values',
        operators: operators,
    },
    {
        propertyLabel: 'Marketplace',
        key: 'marketplaceId',
        groupValuesLabel: 'Marketplace values',
        operators: operators,
    },
    {
        propertyLabel: 'Merchant',
        key: 'merchantCustomerId',
        groupValuesLabel: 'Merchant values',
        operators: operators,
    },
    {
        propertyLabel: 'Program',
        key: 'fulfillmentProgramId',
        groupValuesLabel: 'Program values',
        operators: operators,
    },
    {
        propertyLabel: 'Region',
        key: 'fulfillmentRegionId',
        groupValuesLabel: 'Region values',
        operators: operators,
    },
];

const AcTable: React.FC<TableInput> = ({tableItems, additionalValue, fileName}) => {
    useEffect(() => {
        ReactTooltip.rebuild();
    }, [additionalValue.programDescriptions]);

    const tableDetails: TableDetails = {
        tableId: TABLE_ID,
        header: `Used by ${tableItems.length} ACs`,
        fileName: fileName,
        getRow: function (row: any): string[] {
            return [row.id, row.marketplaceId, row.merchantCustomerId, row.fulfillmentProgramId, row.fulfillmentRegionId];
        },
    };
    return (
        <>
            {useTableCreation(tableDetails, COLUMN_DEFINITIONS(additionalValue.fnsku, additionalValue.programDescriptions), tableItems, FILTERING_PROPERTIES)}
            <ReactTooltip id="program-tooltip" effect="solid"/>
        </>
    );
};

export default AcTable;
